import logger from "../utils/logger";
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Activity, Monitor, Clock, TrendingUp, Users, Plus } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

import API_URL from '../utils/api';

const ActivityPage = () => {
    const [summary, setSummary] = useState(null);
    const [teamProductivity, setTeamProductivity] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState('');
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [loading, setLoading] = useState(true);
    const [logDialogOpen, setLogDialogOpen] = useState(false);
    const [logForm, setLogForm] = useState({
        app_name: '', duration_seconds: 60, is_productive: true, category: 'work', window_title: ''
    });

    const COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

    useEffect(() => { fetchData(); }, [selectedEmployee, selectedDate]);

    const fetchData = async () => {
        try {
            const params = [];
            if (selectedEmployee) params.push(`employee_id=${selectedEmployee}`);
            if (selectedDate) params.push(`date=${selectedDate}`);
            const queryString = params.length ? `?${params.join('&')}` : '';

            const [summaryRes, teamRes, empRes] = await Promise.all([
                axios.get(`${API_URL}/api/activity/summary${queryString}`, { withCredentials: true }),
                axios.get(`${API_URL}/api/activity/team-productivity?date=${selectedDate}`, { withCredentials: true }),
                axios.get(`${API_URL}/api/employees`, { withCredentials: true })
            ]);
            setSummary(summaryRes.data);
            setTeamProductivity(teamRes.data);
            setEmployees(empRes.data.filter(e => e.role !== 'admin'));
        } catch (error) {
            logger.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="flex items-center justify-center h-64"><div className="text-slate-500">Loading activity data...</div></div>;

    const handleLogActivity = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_URL}/api/activity/log`, {
                ...logForm,
                duration_seconds: parseInt(logForm.duration_seconds)
            }, { withCredentials: true });
            setLogDialogOpen(false);
            setLogForm({ app_name: '', duration_seconds: 60, is_productive: true, category: 'work', window_title: '' });
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Error logging activity');
        }
    };

    return (
        <div className="space-y-6" data-testid="activity-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Activity Monitoring</h1>
                    <p className="text-slate-500 mt-1">Track employee productivity and app usage</p>
                </div>
                <div className="flex gap-2">
                    <Dialog open={logDialogOpen} onOpenChange={setLogDialogOpen}>
                        <DialogTrigger asChild>
                            <Button variant="outline" className="rounded-none" data-testid="log-activity-btn"><Plus className="h-4 w-4 mr-2" />Log Activity</Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-none max-w-md">
                            <DialogHeader><DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Log Activity</DialogTitle></DialogHeader>
                            <form onSubmit={handleLogActivity} className="space-y-4 mt-4">
                                <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">App / Tool Name</Label>
                                    <Input value={logForm.app_name} onChange={(e) => setLogForm({...logForm, app_name: e.target.value})} className="rounded-none mt-1" placeholder="e.g. VS Code, Chrome, Slack" required data-testid="activity-app-input" /></div>
                                <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Window Title</Label>
                                    <Input value={logForm.window_title} onChange={(e) => setLogForm({...logForm, window_title: e.target.value})} className="rounded-none mt-1" placeholder="e.g. Project Report" /></div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Duration (seconds)</Label>
                                        <Input type="number" value={logForm.duration_seconds} onChange={(e) => setLogForm({...logForm, duration_seconds: e.target.value})} className="rounded-none mt-1" min="1" required /></div>
                                    <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Category</Label>
                                        <Select value={logForm.category} onValueChange={(v) => setLogForm({...logForm, category: v})}>
                                            <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                                            <SelectContent className="rounded-none">
                                                <SelectItem value="work">Work</SelectItem>
                                                <SelectItem value="communication">Communication</SelectItem>
                                                <SelectItem value="development">Development</SelectItem>
                                                <SelectItem value="meeting">Meeting</SelectItem>
                                                <SelectItem value="break">Break</SelectItem>
                                                <SelectItem value="other">Other</SelectItem>
                                            </SelectContent>
                                        </Select></div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input type="checkbox" checked={logForm.is_productive} onChange={(e) => setLogForm({...logForm, is_productive: e.target.checked})} id="productive" />
                                    <Label htmlFor="productive" className="text-sm">Productive Activity</Label>
                                </div>
                                <div className="flex justify-end gap-3 pt-4">
                                    <Button type="button" variant="outline" className="rounded-none" onClick={() => setLogDialogOpen(false)}>Cancel</Button>
                                    <Button type="submit" className="bg-brand hover:bg-brand rounded-none" data-testid="save-activity-btn">Log Activity</Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                    <Select value={selectedEmployee || "all"} onValueChange={(v) => setSelectedEmployee(v === "all" ? "" : v)}>
                        <SelectTrigger className="w-[180px] rounded-none"><SelectValue placeholder="All Employees" /></SelectTrigger>
                        <SelectContent className="rounded-none">
                            <SelectItem value="all">All Employees</SelectItem>
                            {employees.map(e => <SelectItem key={e._id} value={e._id}>{e.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-[150px] rounded-none" />
                </div>
            </div>

            {summary && (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        <Card className="rounded-none border-slate-200">
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Total Time</p>
                                        <p className="text-3xl font-black text-slate-900 mt-2" style={{ fontFamily: 'Chivo, sans-serif' }}>{Math.round(summary.total_time_minutes)} min</p>
                                    </div>
                                    <div className="p-3 bg-blue-100"><Clock className="h-6 w-6 text-brand" /></div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="rounded-none border-slate-200">
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Productive Time</p>
                                        <p className="text-3xl font-black text-green-600 mt-2" style={{ fontFamily: 'Chivo, sans-serif' }}>{Math.round(summary.productive_time_minutes)} min</p>
                                    </div>
                                    <div className="p-3 bg-green-100"><TrendingUp className="h-6 w-6 text-green-600" /></div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="rounded-none border-slate-200">
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Non-Productive</p>
                                        <p className="text-3xl font-black text-red-600 mt-2" style={{ fontFamily: 'Chivo, sans-serif' }}>{Math.round(summary.non_productive_time_minutes)} min</p>
                                    </div>
                                    <div className="p-3 bg-red-100"><Activity className="h-6 w-6 text-red-600" /></div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="rounded-none border-slate-200">
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Productivity</p>
                                        <p className="text-3xl font-black text-brand mt-2" style={{ fontFamily: 'Chivo, sans-serif' }}>{summary.productivity_percentage}%</p>
                                    </div>
                                    <div className="p-3 bg-blue-100"><Monitor className="h-6 w-6 text-brand" /></div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Card className="rounded-none border-slate-200">
                            <CardHeader className="border-b border-slate-200">
                                <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                    <Monitor className="h-5 w-5 text-brand" />Top Applications
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-6">
                                {summary.app_usage && summary.app_usage.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={300}>
                                        <BarChart data={summary.app_usage} layout="vertical">
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                            <XAxis type="number" tick={{ fontSize: 12 }} stroke="#64748b" />
                                            <YAxis dataKey="app" type="category" tick={{ fontSize: 12 }} stroke="#64748b" width={100} />
                                            <Tooltip contentStyle={{ border: '1px solid #e2e8f0', borderRadius: 0, backgroundColor: '#fff' }} />
                                            <Bar dataKey="duration_minutes" fill="#2563eb" name="Minutes" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="flex items-center justify-center h-[300px] text-slate-500">No activity data available</div>
                                )}
                            </CardContent>
                        </Card>

                        <Card className="rounded-none border-slate-200">
                            <CardHeader className="border-b border-slate-200">
                                <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                    <Activity className="h-5 w-5 text-brand" />Category Distribution
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-6">
                                {summary.category_usage && summary.category_usage.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={300}>
                                        <PieChart>
                                            <Pie
                                                data={summary.category_usage}
                                                cx="50%" cy="50%"
                                                labelLine={false}
                                                label={({ category, percent }) => `${category} (${(percent * 100).toFixed(0)}%)`}
                                                outerRadius={100}
                                                dataKey="duration_minutes"
                                                nameKey="category"
                                            >
                                                {summary.category_usage.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip contentStyle={{ border: '1px solid #e2e8f0', borderRadius: 0, backgroundColor: '#fff' }} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="flex items-center justify-center h-[300px] text-slate-500">No category data available</div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </>
            )}

            <Card className="rounded-none border-slate-200">
                <CardHeader className="border-b border-slate-200">
                    <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        <Users className="h-5 w-5 text-brand" />Team Productivity
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50">
                                <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Employee</th>
                                <th className="text-right p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Total Time</th>
                                <th className="text-right p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Productive</th>
                                <th className="text-right p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Productivity %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {teamProductivity.map((emp) => (
                                <tr key={emp.employee_id || emp.employee_name} className="border-b border-slate-200 hover:bg-slate-50">
                                    <td className="p-4 font-medium text-slate-900">{emp.employee_name}</td>
                                    <td className="p-4 text-right text-slate-700">{Math.round(emp.total_time_minutes || 0)} min</td>
                                    <td className="p-4 text-right text-green-600">{Math.round(emp.productive_time_minutes || 0)} min</td>
                                    <td className="p-4 text-right">
                                        <Badge className={`rounded-none ${(emp.productivity_percentage || 0) >= 70 ? 'bg-green-100 text-green-800' : (emp.productivity_percentage || 0) >= 50 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>
                                            {Math.round(emp.productivity_percentage || 0)}%
                                        </Badge>
                                    </td>
                                </tr>
                            ))}
                            {teamProductivity.length === 0 && <tr><td colSpan="4" className="p-8 text-center text-slate-500">No productivity data for this date. Activity data is recorded via desktop agent.</td></tr>}
                        </tbody>
                    </table>
                </CardContent>
            </Card>

            <Card className="rounded-none border-slate-200 bg-blue-50">
                <CardContent className="p-6">
                    <h3 className="font-bold text-slate-900 mb-2">Desktop Agent Required</h3>
                    <p className="text-slate-600 text-sm">
                        Activity monitoring requires employees to install the DIGI-LOANS HRM desktop agent on their computers. 
                        The agent tracks app usage, captures periodic screenshots, and sends data to this dashboard.
                        Contact your administrator to get the desktop agent installer.
                    </p>
                </CardContent>
            </Card>
        </div>
    );
};

export default ActivityPage;
